// playbookApi.js — Draft Playbook (s.138 NI Act notice etc.) helpers
//
// SSE `event: playbook` →
//   { kind: "draft_checkpoint", matter_id, document_type, mode, ready, blocking,
//     contradictions, next_deadline, title, api: "/playbook/{uid}/matters/{matter_id}" }
//
// Backend anuppura `api`-la user id hard-code aagi varum (549). Adhai use pannaama,
// logged-in userid + matter_id vechu path-ai naama build pannrom.
//
// NOTE: POST / PATCH body shapes kela irukkura BODIES object-la mattum irukku.
// Swagger-la (/docs) ovvoru endpoint-oda "Request body" schema paathu, key names
// vera-ya irundha inga mattum maathunga — panel code-ai thoda vendaam.

import config from "../config/pyconfig";

const MATTER_ID_RE = /\bmat_[0-9a-f]{8,}\b/i;
const MODE_RE = /mode:\s*\**\s*([a-z_]+)/i;

// ── Request bodies (py.juristbot.in reference UI-la paatha shape) ───────────
// Ella mutation-kum `expected_version` = matter.version (optimistic lock).
// Version maari irundha backend 409 tharum → panel reload pannum.
export const BODIES = {
  // PATCH /matters/{mid}/facts/{fid}?mode&document_type   → { action: "confirm" | "retract", expected_version }
  factAction: ({ action, version }) => ({ action, expected_version: version }),
  // POST /matters/{mid}/facts?mode&document_type          → { key, value, expected_version }
  // "Change" button-um idhu dhaan (pudhu value add aagi pazhaiyadhu supersede aagum)
  addFact: ({ key, label, value, version }) =>
    key ? { key, value, expected_version: version } : { label, value, expected_version: version },
  // POST /matters/{mid}/contradictions/{cid}/resolve?mode&document_type
  resolve: ({ factId, value, version }) =>
    factId ? { fact_id: factId, expected_version: version } : { value, expected_version: version },
  // POST /playbook/{uid}/intake  (free-text add / correct facts)
  intake: ({ matterId, details, documentType, mode, version }) => ({
    matter_id: matterId,
    details,
    document_type: documentType,
    mode,
    expected_version: version,
  }),
  // POST /playbook/{uid}/generate
  // reference UI: { matter_id, document_type, mode: "quick" | "professional", session_id }
  generate: ({ matterId, documentType, mode, sessionId }) => ({
    matter_id: matterId,
    document_type: documentType,
    mode,
    ...(sessionId ? { session_id: sessionId } : {}),
  }),
  // POST /drafts/{draft_id}/improve  (reference UI)
  improve: ({ instructions, applyReview = true, version }) => ({
    instructions,
    apply_review: applyReview,
    expected_version: version,
  }),
  // PATCH /drafts/{draft_id}/status  (reference UI — Final review → Approve)
  //   { status: "approved", expected_version, advocate_name, enrolment_no, note, confirm: true }
  status: ({ status, version, advocateName, enrolmentNo, note, confirm }) => ({
    status,
    expected_version: version,
    ...(advocateName ? { advocate_name: advocateName } : {}),
    ...(enrolmentNo ? { enrolment_no: enrolmentNo } : {}),
    ...(note ? { note } : {}),
    ...(confirm !== undefined ? { confirm } : {}),
  }),
  // POST /drafts/{draft_id}/review  (Send to advocate) — guess, Swagger-la confirm pannunga
  requestReview: ({ version, note }) => ({ expected_version: version, ...(note ? { note } : {}) }),
  // PATCH /drafts/{draft_id}/sections/{key}   (guess — Swagger-la body paathu confirm pannunga)
  editSection: ({ paragraphs, version }) => ({ paragraphs, expected_version: version }),
};

// ── SSE event / history helpers ─────────────────────────────────────────────
export const parsePlaybookEvent = (parsed, fallbackUserId) => {
  if (!parsed || typeof parsed !== "object") return {};
  let matterId = parsed.matter_id || null;
  if (!matterId && typeof parsed.api === "string") {
    const m = parsed.api.match(MATTER_ID_RE);
    matterId = m ? m[0] : null;
  }
  return {
    matterId,
    userId: fallbackUserId || null,
    mode: parsed.mode || "professional",
    documentType: parsed.document_type || null,
    title: parsed.title || null,
    ready: !!parsed.ready,
    blocking: Number(parsed.blocking) || 0,
    contradictions: Number(parsed.contradictions) || 0,
    nextDeadline: parsed.next_deadline || null,
  };
};

// Reload / history-la event illana answer text-la irukkura `mat_…` id-ai edukkum
export const findMatterIdInText = (text) => {
  if (typeof text !== "string") return null;
  const m = text.match(MATTER_ID_RE);
  return m ? m[0] : null;
};

export const findModeInText = (text) => {
  if (typeof text !== "string") return null;
  const m = text.match(MODE_RE);
  return m ? m[1].toLowerCase() : null;
};

// ── fetch helpers ───────────────────────────────────────────────────────────
const base = (uid) => `${config.apiBaseUrl}/playbook/${encodeURIComponent(uid)}`;

// Backend error shapes: { detail }, { message }, { error: { code, message } }
const readError = async (res) => {
  try {
    const data = await res.json();
    const e = data?.error;
    if (e && typeof e === "object" && e.message) return { message: e.message, code: e.code };
    const d = data?.detail ?? data?.message ?? e;
    if (typeof d === "string") return { message: d };
    if (d && typeof d === "object" && d.message) return { message: d.message, code: d.code };
    if (Array.isArray(d)) return { message: d.map((x) => x?.msg || JSON.stringify(x)).join("; ") };
    return { message: JSON.stringify(data) };
  } catch {
    return { message: `${res.status} ${res.statusText}` };
  }
};

const httpError = async (res) => {
  const { message, code } = await readError(res);
  const err = new Error(message);
  err.status = res.status;
  err.code = code;
  return err;
};

const request = async (url, { method = "GET", body, signal } = {}) => {
  const res = await fetch(url, {
    method,
    signal,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await httpError(res);
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
};

const qs = (obj) => {
  const p = new URLSearchParams();
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

// ── API ─────────────────────────────────────────────────────────────────────
// GET /playbook/{uid}/matters/{mid}?mode=professional&document_type=ni138_demand_notice
export const getMatter = (uid, matterId, { mode, documentType } = {}, signal) =>
  request(
    `${base(uid)}/matters/${matterId}${qs({ mode, document_type: documentType })}`,
    { signal }
  );

const mq = (q) => qs({ mode: q?.mode, document_type: q?.documentType });

// q = { mode, documentType } — ella matter call-kum query-la pogum
export const resolveContradiction = (uid, matterId, cid, args, q) =>
  request(`${base(uid)}/matters/${matterId}/contradictions/${cid}/resolve${mq(q)}`, {
    method: "POST",
    body: BODIES.resolve(args),
  });

export const factAction = (uid, matterId, factId, args, q) =>
  request(`${base(uid)}/matters/${matterId}/facts/${factId}${mq(q)}`, {
    method: "PATCH",
    body: BODIES.factAction(args),
  });

export const addFact = (uid, matterId, args, q) =>
  request(`${base(uid)}/matters/${matterId}/facts${mq(q)}`, {
    method: "POST",
    body: BODIES.addFact(args),
  });

export const intake = (uid, args) =>
  request(`${base(uid)}/intake`, { method: "POST", body: BODIES.intake(args) });

export const generateDraft = (uid, args) =>
  request(`${base(uid)}/generate`, { method: "POST", body: BODIES.generate(args) });

export const getTypes = () => request(`${config.apiBaseUrl}/playbook/types`);

export const getDraft = (uid, draftId) => request(`${base(uid)}/drafts/${draftId}`);

export const editSection = (uid, draftId, key, args) =>
  request(`${base(uid)}/drafts/${draftId}/sections/${encodeURIComponent(key)}`, {
    method: "PATCH",
    body: BODIES.editSection(args),
  });

export const improveDraft = (uid, draftId, args) =>
  request(`${base(uid)}/drafts/${draftId}/improve`, { method: "POST", body: BODIES.improve(args) });

export const setDraftStatus = (uid, draftId, args) =>
  request(`${base(uid)}/drafts/${draftId}/status`, { method: "PATCH", body: BODIES.status(args) });

export const requestReview = (uid, draftId, args) =>
  request(`${base(uid)}/drafts/${draftId}/review`, { method: "POST", body: BODIES.requestReview(args) });

// DELETE /drafts/{draft_id}/review?expected_version=N
export const cancelReview = (uid, draftId, version) =>
  request(`${base(uid)}/drafts/${draftId}/review${qs({ expected_version: version })}`, { method: "DELETE" });

// POST /drafts/{draft_id}/review/seen — advocate decision-ai user paathaachu nu mark
export const markReviewSeen = (uid, draftId) =>
  request(`${base(uid)}/drafts/${draftId}/review/seen`, { method: "POST" });

// GET /drafts/{draft_id}/terms/{term}
export const explainTerm = (uid, draftId, term) =>
  request(`${base(uid)}/drafts/${draftId}/terms/${encodeURIComponent(term)}`);

// GET /drafts/{draft_id}/export?format=docx|pdf → file download
export const exportDraft = async (uid, draftId, format = "docx", fallbackName = "draft") => {
  const res = await fetch(`${base(uid)}/drafts/${draftId}/export${qs({ format })}`);
  if (!res.ok) throw await httpError(res);
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const name = m ? decodeURIComponent(m[1]) : `${fallbackName}.${format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
